import type { CallMessage, TranscriptChunk } from '@/types/call';

export type ManagerMonitorStatus =
  | 'idle'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'ended'
  | 'error';

export interface ManagerTranscriptMonitorState {
  status: ManagerMonitorStatus;
  reconnectAttempt: number;
  callerPhoneNumber: string;
  callerName: string;
  previousMessages: CallMessage[];
  transcriptChunks: TranscriptChunk[];
}

export type ManagerMonitorInitEvent = {
  type: 'init';
  previousMessages?: CallMessage[];
  callerPhoneNumber?: string;
  callerName?: string;
  bufferedTranscripts?: TranscriptChunk[];
  caseId?: number;
  workspaceId?: number;
  recordingState?: 'enabled' | 'paused';
  isDirectConnect?: boolean;
};

export type ManagerMonitorTranscriptEvent = {
  type: 'transcript';
  transcript: string;
  isPartial: boolean;
  timestamp: string;
  speaker?: 'agent' | 'customer' | 'summary';
  speakerObject?: object;
  resultId?: string;
  startTime?: number;
  endTime?: number;
  sentenceId?: string;
};

export type ManagerMonitorPingEvent = {
  type: 'ping';
  timestamp?: string;
};

export type ManagerMonitorStreamEndEvent = {
  type: 'stream_end';
};

export type ManagerMonitorEvent =
  | ManagerMonitorInitEvent
  | ManagerMonitorTranscriptEvent
  | ManagerMonitorPingEvent
  | ManagerMonitorStreamEndEvent;

export const managerTranscriptMonitorInitialState: ManagerTranscriptMonitorState =
  {
    status: 'idle',
    reconnectAttempt: 0,
    callerPhoneNumber: '',
    callerName: '',
    previousMessages: [],
    transcriptChunks: [],
  };

export type ManagerTranscriptMonitorAction =
  | { type: 'reset' }
  | { type: 'connecting' }
  | { type: 'live' }
  | { type: 'reconnecting'; attempt: number }
  | { type: 'error' }
  | { type: 'stream_end' }
  | { type: 'event'; event: ManagerMonitorEvent };

function upsertTranscriptChunk(
  chunks: TranscriptChunk[],
  chunk: TranscriptChunk
): TranscriptChunk[] {
  if (chunk.isPartial && chunk.resultId) {
    const idx = chunks.findIndex(
      c => c.isPartial && c.resultId === chunk.resultId
    );
    if (idx >= 0) {
      const next = [...chunks];
      next[idx] = chunk;
      return next;
    }
    return [...chunks, chunk];
  }

  if (chunk.isPartial) {
    const next = [...chunks];
    const lastIdx = next.length - 1;
    if (lastIdx >= 0 && next[lastIdx]?.isPartial) {
      next[lastIdx] = chunk;
      return next;
    }
    return [...next, chunk];
  }

  if (!chunk.isPartial) {
    const next = [...chunks];

    if (chunk.sentenceId) {
      const sentenceIdx = next.findIndex(
        c => c.sentenceId === chunk.sentenceId
      );
      if (sentenceIdx >= 0) {
        next[sentenceIdx] = chunk;
      } else {
        const lastIdx = next.length - 1;
        if (lastIdx >= 0 && next[lastIdx]?.isPartial) {
          next[lastIdx] = chunk;
        } else {
          next.push(chunk);
        }
      }
    } else if (chunk.resultId) {
      const resultIdx = next.findIndex(c => c.resultId === chunk.resultId);
      if (resultIdx >= 0) {
        next[resultIdx] = chunk;
      } else {
        next.push(chunk);
      }
    } else {
      next.push(chunk);
    }

    if (chunk.resultId) {
      return next.filter(c => !(c.isPartial && c.resultId === chunk.resultId));
    }
    return next;
  }

  return [...chunks, chunk];
}

export function reduceManagerTranscriptState(
  state: ManagerTranscriptMonitorState,
  action: ManagerTranscriptMonitorAction
): ManagerTranscriptMonitorState {
  switch (action.type) {
    case 'reset':
      return managerTranscriptMonitorInitialState;
    case 'connecting':
      return { ...state, status: 'connecting', reconnectAttempt: 0 };
    case 'live':
      return { ...state, status: 'live', reconnectAttempt: 0 };
    case 'reconnecting':
      return {
        ...state,
        status: 'reconnecting',
        reconnectAttempt: action.attempt,
      };
    case 'error':
      return { ...state, status: 'error' };
    case 'stream_end':
      return { ...state, status: 'ended' };
    case 'event': {
      const evt = action.event;
      if (evt.type === 'init') {
        const normalizedBuffered = (evt.bufferedTranscripts ?? []).reduce(
          (acc, chunk) => upsertTranscriptChunk(acc, chunk),
          [] as TranscriptChunk[]
        );
        return {
          ...state,
          status: 'live',
          reconnectAttempt: 0,
          callerPhoneNumber: evt.callerPhoneNumber ?? '',
          callerName: evt.callerName ?? '',
          previousMessages: evt.previousMessages ?? [],
          transcriptChunks: normalizedBuffered,
        };
      }
      if (evt.type === 'transcript') {
        return {
          ...state,
          status: 'live',
          transcriptChunks: upsertTranscriptChunk(state.transcriptChunks, {
            transcript: evt.transcript,
            isPartial: evt.isPartial,
            timestamp: evt.timestamp,
            speaker: evt.speaker,
            speakerObject:
              evt.speakerObject as TranscriptChunk['speakerObject'],
            resultId: evt.resultId,
            startTime: evt.startTime,
            endTime: evt.endTime,
            sentenceId: evt.sentenceId,
          }),
        };
      }
      if (evt.type === 'stream_end') {
        return { ...state, status: 'ended' };
      }
      return state;
    }
    default:
      return state;
  }
}

export function parseSseEventBlock(block: string): ManagerMonitorEvent | null {
  const dataLines = block
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice(5).trim());

  if (dataLines.length === 0) return null;
  const payload = dataLines.join('\n');
  try {
    return JSON.parse(payload) as ManagerMonitorEvent;
  } catch {
    return null;
  }
}
